/**
 * /customers/duplicates — Org-wide de-dup queue.
 *
 * Manager+ only. Lists clusters of customer records that look like duplicates,
 * with the most-tickets row as the primary and merge buttons on each candidate.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  GitMerge, Phone, Mail, Users2, ArrowLeft, RefreshCw, AlertTriangle, ExternalLink,
  Undo2, Clock,
} from "lucide-react";
import { toast } from "sonner";
import MergeCustomerDialog from "@/components/customers/MergeCustomerDialog";

const REASON_LABEL = {
  same_email: { text: "Same email",  tone: "bg-rose-50 text-rose-700 border-rose-200" },
  phone_near: { text: "Phone match", tone: "bg-amber-50 text-amber-700 border-amber-200" },
  name_fuzzy: { text: "Similar name",tone: "bg-sky-50 text-sky-700 border-sky-200" },
};

export default function CustomerDuplicates() {
  const [clusters, setClusters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mergeFor, setMergeFor] = useState(null);     // {primary, secondary}
  const [refreshTick, setRefreshTick] = useState(0);
  const [recentMerges, setRecentMerges] = useState([]);
  const [undoWindowSec, setUndoWindowSec] = useState(900);

  const load = async () => {
    setLoading(true);
    try {
      const [dupRes, rmRes] = await Promise.all([
        api.get("/customers/duplicates", { params: { limit: 50 } }),
        api.get("/customers/recent-merges"),
      ]);
      setClusters(dupRes.data.clusters || []);
      setRecentMerges(rmRes.data.rows || []);
      setUndoWindowSec(rmRes.data.window_seconds || 900);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load duplicates");
    } finally {
      setLoading(false);
    }
  };

  const undoMerge = async (m) => {
    if (!window.confirm(`Undo the merge of "${m.secondary_name || m.before?.secondary_name}" into the primary? This will restore the secondary customer and re-point its tickets.`)) return;
    try {
      const r = await api.post(`/customers/undo-merge/${m.id}`);
      toast.success(`Restored · ${r.data.tickets_restored} ticket(s) repointed`);
      setRefreshTick((t) => t + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Undo failed");
    }
  };

  useEffect(() => { load(); }, [refreshTick]);

  return (
    <div className="space-y-4" data-testid="customer-duplicates-page">
      <div className="flex items-center justify-between">
        <div>
          <Link to="/customers" className="inline-flex items-center text-sm text-slate-500 hover:text-slate-800">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to customers
          </Link>
          <h1 className="font-heading text-2xl font-semibold text-slate-900 mt-1 flex items-center gap-2">
            <Users2 className="w-5 h-5 text-indigo-600" /> Duplicate customer queue
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Customers grouped by shared email or matching phone tails (last 8 digits).
            The row with the most tickets is suggested as the primary.
          </p>
        </div>
        <Button variant="outline" onClick={() => setRefreshTick((t) => t + 1)} data-testid="dup-refresh-btn">
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
        </Button>
      </div>

      {loading && <div className="text-sm text-slate-400">Scanning…</div>}

      {/* Phase 5.8j — Recent merges within the undo window */}
      {recentMerges.length > 0 && (
        <Card className="border-amber-200 bg-amber-50/40" data-testid="recent-merges-banner">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-sm text-amber-800 flex items-center gap-2">
              <Undo2 className="w-4 h-4" /> Recently merged
              <span className="text-[10px] uppercase tracking-wider text-amber-700 font-normal flex items-center gap-1">
                <Clock className="w-3 h-3" /> {Math.floor(undoWindowSec / 60)}-min undo window
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="-mt-1 space-y-1.5">
            {recentMerges.map((m) => (
              <RecentMergeRow key={m.id} m={m} onUndo={() => undoMerge(m)} windowSec={undoWindowSec} />
            ))}
          </CardContent>
        </Card>
      )}

      {!loading && clusters.length === 0 && (
        <Card className="border-slate-200">
          <CardContent className="p-8 text-center" data-testid="dup-empty-state">
            <div className="text-4xl mb-2">🎯</div>
            <div className="font-heading text-base font-semibold text-slate-700">No duplicates found</div>
            <p className="text-xs text-slate-500 mt-1">
              Your customer list looks clean. We re-scan on every refresh.
            </p>
          </CardContent>
        </Card>
      )}

      <div className="space-y-3">
        {clusters.map((cluster) => (
          <ClusterCard
            key={cluster.primary.id + cluster.match_key}
            cluster={cluster}
            onMerge={(secondary) => setMergeFor({ primary: cluster.primary, secondary })}
          />
        ))}
      </div>

      <MergeCustomerDialog
        open={!!mergeFor}
        onOpenChange={(v) => { if (!v) setMergeFor(null); }}
        primary={mergeFor?.primary}
        secondary={mergeFor?.secondary}
        onMerged={() => setRefreshTick((t) => t + 1)}
      />
    </div>
  );
}

function ClusterCard({ cluster, onMerge }) {
  const { primary, candidates, match_key } = cluster;
  const isEmail = match_key?.startsWith("email:");
  return (
    <Card className="border-slate-200">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="font-heading text-sm flex items-center gap-2">
            <Badge variant="outline" className={isEmail ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-amber-50 text-amber-700 border-amber-200"}>
              {isEmail ? <Mail className="w-3 h-3 mr-1" /> : <Phone className="w-3 h-3 mr-1" />}
              {isEmail ? match_key.slice(6) : `…${match_key.slice(6)}`}
            </Badge>
            <span className="text-xs text-slate-500 font-normal">{1 + candidates.length} matching records</span>
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className="-mt-1 space-y-2">
        {/* Primary row */}
        <CustomerRow row={{ ...primary, _is_primary: true }} />
        {/* Candidates */}
        {candidates.map((c) => (
          <CustomerRow key={c.id} row={c} onMerge={() => onMerge(c)} />
        ))}
      </CardContent>
    </Card>
  );
}

function CustomerRow({ row, onMerge }) {
  const reasons = row.match_reasons || [];
  return (
    <div
      className={`flex flex-wrap items-center gap-3 px-3 py-2 rounded border ${
        row._is_primary ? "border-emerald-300 bg-emerald-50/30" : "border-slate-200"
      }`}
      data-testid={`dup-row-${row.id}`}
    >
      {row._is_primary && (
        <Badge variant="outline" className="bg-emerald-100 text-emerald-700 border-emerald-300 shrink-0">
          PRIMARY
        </Badge>
      )}
      <div className="flex-1 min-w-[220px]">
        <Link to={`/customers/${row.id}`} className="text-sm font-medium text-slate-900 hover:text-indigo-600">
          {row.name || "(no name)"}
          <ExternalLink className="w-3 h-3 inline ml-1 opacity-50" />
        </Link>
        <div className="text-[11px] text-slate-500 flex items-center gap-2 mt-0.5 flex-wrap">
          <span className="font-mono">{row.phone_e164 || row.phone || "—"}</span>
          {row.email && <><span>·</span><span>{row.email}</span></>}
        </div>
      </div>
      <div className="flex items-center gap-1.5">
        <Badge variant="outline" className="bg-slate-50 text-slate-700 text-[10px]">
          {row.ticket_count || 0} ticket{row.ticket_count === 1 ? "" : "s"}
        </Badge>
        {reasons.map((r) => {
          const meta = REASON_LABEL[r] || { text: r, tone: "bg-slate-50 text-slate-700 border-slate-200" };
          return (
            <Badge key={r} variant="outline" className={`${meta.tone} text-[10px]`}>
              {meta.text}
            </Badge>
          );
        })}
      </div>
      {!row._is_primary && onMerge && (
        <Button size="sm" onClick={onMerge}
                className="bg-indigo-600 hover:bg-indigo-700 h-8 text-xs"
                data-testid={`merge-btn-${row.id}`}>
          <GitMerge className="w-3.5 h-3.5 mr-1" /> Merge into primary
        </Button>
      )}
    </div>
  );
}


function RecentMergeRow({ m, onUndo, windowSec }) {
  const created = new Date(m.created_at);
  const ageSec = Math.max(0, Math.floor((Date.now() - created.getTime()) / 1000));
  const remainingSec = Math.max(0, windowSec - ageSec);
  const remainingLabel = remainingSec >= 60
    ? `${Math.floor(remainingSec / 60)}m ${remainingSec % 60}s`
    : `${remainingSec}s`;
  const expired = remainingSec === 0;
  return (
    <div className="flex flex-wrap items-center gap-3 px-3 py-2 rounded border border-amber-200 bg-white"
         data-testid={`recent-merge-${m.id}`}>
      <div className="flex-1 min-w-[180px]">
        <div className="text-xs text-slate-800">
          <span className="font-medium">{m.secondary_name || m.before?.secondary_name || "(unknown)"}</span>
          <span className="text-slate-500"> → primary</span>
        </div>
        <div className="text-[10px] text-slate-500">
          by {m.user_name || m.user_email} · {(m.after?.tickets_moved || 0)} ticket(s) moved
        </div>
      </div>
      <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${
        expired ? "bg-slate-100 text-slate-500" : "bg-amber-100 text-amber-800"
      }`} title={`Merged ${created.toLocaleTimeString()}`}>
        {expired ? "expired" : `${remainingLabel} left`}
      </span>
      <Button size="sm" variant="outline" onClick={onUndo} disabled={expired}
              className="h-8 text-xs border-amber-300 text-amber-800 hover:bg-amber-50"
              data-testid={`undo-merge-btn-${m.id}`}>
        <Undo2 className="w-3.5 h-3.5 mr-1" /> Undo
      </Button>
    </div>
  );
}
